import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from 'axios';
import CorporateHeader from "../../components/corporatedashboard/CorporateHeader";
import HealthWorkerDashboardSidebar from "../../components/global/HealthWorkerSidebar";
import { useStateProvider } from "../../manager/StateContext";
import { formatDates, DetailItem } from "../../utils/helpers";
import LoadingModal from "../../components/Modal/LoadingModal";
import { FiUser, FiPhone, FiMail, FiCalendar, FiMapPin, FiFileText, FiUpload, FiX, FiActivity, FiChevronLeft, FiDownload, FiPaperclip, FiImage, FiFile, FiExternalLink } from "react-icons/fi";


const HealthWorkerVisitDetails = () => {
  const [visitData, setVisitData] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState({
    location: "",
    summary: "",
    recommend: "",
    uploads: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [newUploadedFiles, setNewUploadedFiles] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('');

  const { id: visitId } = useParams();
  const navigate = useNavigate();
  const [{ stage_token }, dispatch] = useStateProvider();

  useEffect(() => {
    if (visitId) {
      fetchVisitDetails();
    }
  }, [visitId, stage_token]);

  // Toast message timeout effect
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const fetchVisitDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_LIVE_URL}/homecare-visit-report/${visitId}/report`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${stage_token}`,
          },
        }
      );
     
      const visit = response.data || {};
      console.log("Visit Data:", visit.visitDetails?.contract_id);
      setVisitData(visit);

      // Process existing uploads
      let existingUploads = [];
      if (visit.reportDetails?.upload) {
        // Check if the upload is a string that looks like JSON or an array
        if (typeof visit.reportDetails.upload === 'string' && 
            (visit.reportDetails.upload.startsWith('[') || visit.reportDetails.upload.includes(','))) {
          try {
            //  parse the JSON string if it's in that format
            const parsedUploads = JSON.parse(visit.reportDetails.upload);
            existingUploads = Array.isArray(parsedUploads) ? parsedUploads : [visit.reportDetails.upload];
          } catch (e) {
            // If parsing fails, split by comma (fallback)
            existingUploads = visit.reportDetails.upload
              .replace(/[\[\]"'\s]/g, '') // Remove brackets, quotes and spaces
              .split(',')
              .filter(item => item.trim() !== '');
          }
        } else if (Array.isArray(visit.reportDetails.upload)) {
          // If already an array
          existingUploads = visit.reportDetails.upload;
        } else if (typeof visit.reportDetails.upload === 'string') {
          // If a single string
          existingUploads = [visit.reportDetails.upload];
        }
      }

      // Initialize report data with existing or empty values
      setReportData({
        location: visit.reportDetails?.location || '',
        summary: visit.reportDetails?.summary || '',
        recommend: visit.reportDetails?.recommendation || '',
        uploads: existingUploads
      });

      // Fetch patient and contract data
      const contractId = visit.visitDetails?.contract_id?._id || visit.contract_id?._id || visit.contract_id;
      
      if (contractId) {
        try {
          const contractResponse = await axios.get(
            `${process.env.REACT_APP_API_LIVE_URL}/dashboard/homecare-booking/CTR-${contractId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${stage_token}`,
              },
            }
          );
          
          console.log("Contract Data:", contractResponse.data?.contractDetails);  
          setContractData(contractResponse.data?.contractDetails || {});
          setPatientData(contractResponse.data?.contractDetails?.patient || {});
        } catch (contractError) {
          console.error("Error fetching contract details:", contractError);

        }
      }

    } catch (error) {
      console.error("Error fetching visit details:", error);
      setError("Failed to load visit details. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReportData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      
      // Validate file types
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
      
      if (invalidFiles.length > 0) {
        setSubmitError(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Only JPG, PNG, PDF, DOC, and DOCX files are allowed.`);
        return;
      }
      
      // Check file sizes
      const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        setSubmitError(`File size limit exceeded: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum file size is 5MB.`);
        return;
      }
      
      // Add new files to state
      setNewUploadedFiles(prev => [...prev, ...files]);
      setSubmitError(null);
    }
  };

  const removeExistingFile = (index) => {
    setReportData(prev => {
      const updatedUploads = [...prev.uploads];
      updatedUploads.splice(index, 1);
      return {
        ...prev,
        uploads: updatedUploads
      };
    });
  };

  const removeNewFile = (index) => {
    setNewUploadedFiles(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };
  
  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
  
    // Validate required fields
    if (!reportData.summary?.trim()) {
      setSubmitError("Please provide a summary of care provided");
      return;
    }
  
    if (!reportData.recommend?.trim()) {
      setSubmitError("Please provide recommendations");
      return;
    }
  
    setIsSubmitting(true);
  
    try {
      const formData = new FormData();
      
      // Get the appropriate contract ID
      const contractId = visitData.visitDetails?.contract_id?._id || 
                         visitData.contract_id?._id || 
                         visitData.contract_id;
      
      // Add text fields
      formData.append('contract_id', contractId);
      formData.append('visit_id', visitId);
      formData.append('summary', reportData.summary.trim());
      formData.append('recommend', reportData.recommend.trim());
      formData.append('location', reportData.location.trim());
      
      // Add carer_id if available
      const carerId = visitData.visitDetails?.carer?._id || visitData.carer?._id;
      if (carerId) {
        formData.append('carer_id', carerId);
      }
  
      // Add existing files (as string array)
      if (reportData.uploads.length > 0) {
        formData.append('existing_uploads', JSON.stringify(reportData.uploads));
      }
  
      // Handle the case when no new files are uploaded
      if (newUploadedFiles.length > 0) {
        newUploadedFiles.forEach((file) => {
          formData.append('upload', file); 
        });
      } else {
        formData.append('upload', null); // Append null to indicate no new files
      }
  
      // Submit the form data
      const response = await axios.post(
        `${process.env.REACT_APP_API_LIVE_URL}/api/submit-homecareReport`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${stage_token}`,
          },
        }
      );
  
      // Show success toast before refreshing data
      showToast('Report submitted successfully!', 'success');
      
      // Set global success message
      dispatch({
        type: 'SET_STATE_MESSAGE',
        payload: {
          type: 'success',
          message: 'Report submitted successfully!'
        }
      });
      
      // Clear the new uploads
      setNewUploadedFiles([]);
      
  
      setTimeout(() => {
        fetchVisitDetails(); // Refresh data
      }, 1000);
  
    } catch (error) {
      console.error("Submission error:", error);
      let errorMessage = "Failed to submit report. Please try again.";
      
      if (error.response) {
        if (error.response.data?.desc === "Invalid file type.") {
          errorMessage = "Invalid file type. Please ensure files are JPG, PNG, PDF, DOC, or DOCX format.";
        } else {
          errorMessage = error.response.data?.message || error.response.data?.desc || error.message;
          if (error.response.data?.errors) {
            errorMessage = Object.values(error.response.data.errors).join(', ');
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFileIcon = (filename) => {
    if (isImageFile(filename)) {
      return <FiImage className="text-blue-500" size={24} />;
    } else if (filename.match(/\.pdf$/i)) {
      return <FiFileText className="text-red-500" size={24} />;
    } else if (filename.match(/\.(doc|docx)$/i)) {
      return <FiFileText className="text-blue-700" size={24} />;
    }
    return <FiFile className="text-gray-500" size={24} />;
  };

  const isImageFile = (filename) => {
    if (typeof filename === 'string') {
      return filename.match(/\.(jpeg|jpg|gif|png)$/i);
    } else if (filename instanceof File) {
      return filename.type.startsWith('image/');
    }
    return false;
  };

  const getFileUrl = (filename) => {
    if (typeof filename === 'string') {
      // Clean up the filename in case it contains unwanted characters
      const cleanFilename = filename.trim().replace(/^["']+|["']+$/g, ''); // Remove quotes
      return `${process.env.REACT_APP_API_LIVE_URL}/uploads/medreport/${cleanFilename}`;
    }
    return '';
  };

  const getFileName = (file) => {
    if (typeof file === 'string') {
      return file.trim().replace(/^["']+|["']+$/g, ''); // Remove quotes
    } else if (file instanceof File) {
      return file.name;
    }
    return 'Unknown File';
  };

  if (isLoading) {
    return (
      <LoadingModal
        open={isLoading}
        setOpen={setIsLoading}
        title="We're fetching your visit details"
      />
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  if (!visitData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500 text-lg">No visit data found</div>
      </div>
    );
  }

  // Safely access nested properties
  const visitDetails = visitData.visitDetails || {};
  const carerInfo = visitDetails.carer || {};

  return (
    <div className="flex justify-between bg-[#F5F7FA] font-Montserrat h-screen">
      <HealthWorkerDashboardSidebar isOpen={false} setOpen={() => {}} />
      
      <div className="w-full h-screen overflow-y-auto custom-scrollbar">
        <CorporateHeader isOpen={false} setOpen={() => {}} />

        {/* Toast notification */}
        {toastMessage && (
          <div className={`fixed top-20 right-4 z-50 p-4 rounded-md shadow-lg max-w-sm ${
            toastType === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            <div className="flex justify-between items-center">
              <span className="font-medium">{toastMessage}</span>
              <button 
                onClick={() => setToastMessage(null)}
                className="ml-4 text-gray-500 hover:text-gray-700"
              >
                <FiX size={18} />
              </button>
            </div>
          </div>
        )}

        <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
            >
              <FiChevronLeft className="mr-1" /> Back to Visits
            </button>

            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold text-[#2D3748]">Visit Details</h1>
            </div>

            {/* Visit Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">Visit Information</h2>
                <div className="space-y-3">
                  <DetailItem label="Visit Start Date" value={formatDates(visitDetails.visit_date)} />
                  <DetailItem label="Visit End Date" value={formatDates(visitDetails.visit_end_date)} />
                  <div className="flex items-start">
                    <span className="text-sm font-medium text-gray-600 w-32">Status</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      visitDetails.visit_status === 1 ? 'bg-green-100 text-green-800' : 
                      visitDetails.visit_status === 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {visitDetails.visit_status === 1 ? 'Completed' : 
                       visitDetails.visit_status === 0 ? 'Pending' : 'Cancelled'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold capitalize mb-4 text-gray-800">Health Worker</h2>
                <div className="space-y-3 capitalize">
                  <DetailItem 
                    label="Name" 
                    value={carerInfo.name || `${carerInfo.first_name || 'N/A'} ${carerInfo.last_name || ''}`.trim()} 
                  />
                  <DetailItem label="Email" value={carerInfo.email || 'N/A'} />
                  <DetailItem label="Contact" value={carerInfo.mobile || 'N/A'} />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">Care Details</h2>
                <div className="space-y-3">
                  <DetailItem label="Care Type" value={contractData?.care_type || 'N/A'} />
                  <DetailItem label="Care Category" value={contractData?.care_category || 'N/A'} />
                </div>
              </div>
            </div>

            {/* Patient Information */}
            {patientData && (
              <div className="bg-white rounded-lg shadow p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">Patient Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <DetailItem icon={<FiUser />} label="Full Name" value={`${patientData.first_name || ''} ${patientData.last_name || ''}`.trim() || 'N/A'} />
                  <DetailItem icon={<FiPhone />} label="Contact" value={patientData.mobile || 'N/A'} />
                  <DetailItem icon={<FiMail />} label="Email" value={patientData.email || 'N/A'} />
                  <DetailItem icon={<FiCalendar />} label="Date of Birth" value={patientData.dateOfBirth ? formatDates(patientData.dateOfBirth) : 'N/A'} />
                  <DetailItem icon={<FiMapPin />} label="Address" value={contractData?.consultation_address || patientData.address || 'N/A'} />
                  <DetailItem icon={<FiActivity />} label="Health Condition" value={contractData?.health_description || 'N/A'} />
                </div>
              </div>
            )}

            {/* Report Form - Always Visible */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-6 text-gray-800">
                {visitData.reportDetails ? 'Edit Visit Report' : 'Create Visit Report'}
              </h2>

              <form onSubmit={handleSubmit}>
                {submitError && (
                  <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                    {submitError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location of Visit <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={reportData.location}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter visit location"
                  
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Summary of Care Provided <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="summary"
                      value={reportData.summary}
                      onChange={handleInputChange}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe the care provided during the visit"
                   
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recommendations <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="recommend"  
                      value={reportData.recommend}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter any recommendations for follow-up care"
                      
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload Report Files
                    </label>
                    <div className="mt-1 flex items-center">
                      <label className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                        <FiUpload className="inline mr-2" />
                        Choose Files
                        <input
                          type="file"
                          name="upload"
                          className="sr-only"
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          multiple
                        />
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      PDF, DOC, DOCX, JPG, PNG (Max 5MB per file)
                    </p>
                  </div>

                  {/* Existing files display */}
                  {reportData.uploads && reportData.uploads.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Existing Files:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {reportData.uploads.map((file, index) => (
                          <div 
                            key={`existing-${index}`}
                            className="border border-gray-200 rounded-md overflow-hidden bg-white hover:shadow-md transition-shadow"
                          >
                            <div className="p-4 flex flex-col items-center">
                              {getFileIcon(file)}
                              <span className="mt-2 text-sm text-center truncate w-full">
                                {getFileName(file)}
                              </span>
                            </div>
                            <div className="border-t border-gray-100 flex justify-between items-center px-3 py-2 bg-gray-50">
                              <a
                                href={getFileUrl(file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                              >
                                {isImageFile(file) ? (
                                  <>
                                    <FiExternalLink className="mr-1" size={12} /> View
                                  </>
                                ) : (
                                  <>
                                    <FiDownload className="mr-1" size={12} /> Download
                                  </>
                                )}
                              </a>
                              <button
                                type="button"
                                onClick={() => removeExistingFile(index)}
                                className="text-xs text-red-500 hover:text-red-700 flex items-center"
                              >
                                <FiX className="mr-1" size={12} /> Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New uploaded files display */}
                  {newUploadedFiles.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">New Files to Upload:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {newUploadedFiles.map((file, index) => (
                          <div 
                            key={`new-${index}`}
                            className="border border-gray-200 rounded-md overflow-hidden bg-white hover:shadow-md transition-shadow"
                          >
                            <div className="p-4 flex flex-col items-center">
                              {getFileIcon(file.name)}
                              <span className="mt-2 text-sm text-center truncate w-full">
                                {file.name}
                              </span>
                            </div>
                            <div className="border-t border-gray-100 flex justify-end items-center px-3 py-2 bg-gray-50">
                              <button
                                type="button"
                                onClick={() => removeNewFile(index)}
                                className="text-xs text-red-500 hover:text-red-700 flex items-center"
                              >
                                <FiX className="mr-1" size={12} /> Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="mt-6 flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {visitData.reportDetails ? 'Updating...' : 'Submitting...'}
                      </>
                    ) : visitData.reportDetails ? 'Update Report' : 'Submit Report'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthWorkerVisitDetails;